import { useState } from "react";
import { CheckboxCard } from "shared/components";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  Briefcase02Icon,
  Mail02Icon,
  PieChart07Icon,
  PiggyBankIcon
} from "@hugeicons-pro/core-stroke-rounded";
import { IncomeType } from "shared/types";
import { CombinedIncomeLayout } from "../components/Layout";
import { CombinedIncomeSubStep } from "../../../constants";
import { toast } from "react-toastify";
import { useCombinedIncome } from "../context";
import { getErrorMessage } from "utils/error";

const incomeTypeOptions = [
  {
    type: IncomeType.Employment,
    label: "Employment",
    icon: Briefcase02Icon
  },
  {
    type: IncomeType.SelfEmployment,
    label: "Self-employment",
    icon: PieChart07Icon
  },
  {
    type: IncomeType.OtherIncome,
    label: "Other income",
    icon: PiggyBankIcon
  },
  {
    type: IncomeType.OfferLetter,
    label: "Offer letter",
    icon: Mail02Icon
  }
];

export const SelectIncomeTypeStep = () => {
  const { updateStepMutation, getNextStep, stepData, refetchVerification } =
    useCombinedIncome();
  const [isProcessing, setIsProcessing] = useState(false);

  const handleSelectIncomeType = async (incomeType: IncomeType) => {
    if (isProcessing) return;

    setIsProcessing(true);
    try {
      const nextStep = getNextStep(CombinedIncomeSubStep.SelectIncomeType, {
        incomeType
      });

      if (!nextStep) return;

      const resetStepData =
        incomeType === stepData.currentIncomeType
          ? {}
          : {
              currentIncomeId: null,
              company_name: "",
              job_title: "",
              start_date: "",
              supervisor: {
                first_name: "",
                last_name: "",
                contact_email: "",
                contact_phone: ""
              },
              description: "",
              paid_from: "",
              pay_period: "",
              amount: ""
            };

      updateStepMutation.mutate(
        {
          lastStep: nextStep,
          currentIncomeType: incomeType,
          ...resetStepData
        },
        {
          onSuccess: () => {
            refetchVerification();
          }
        }
      );
    } catch (error) {
      toast.error(getErrorMessage(error) || "Failed to process income type");
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <CombinedIncomeLayout title="What is applicant's current income?">
      <div className="space-y-3">
        {incomeTypeOptions.map((option) => (
          <CheckboxCard
            key={option.type}
            checked={false}
            value={option.type}
            id={option.label}
            label={
              <div className="flex items-center gap-4">
                <div className="flex justify-center items-center bg-primary-20 w-10 h-10 rounded-lg">
                  <HugeiconsIcon
                    icon={option.icon}
                    size={24}
                    className="text-primary-500"
                  />
                </div>
                <p className="text-base text-alpha-900 font-bold">
                  {option.label}
                </p>
              </div>
            }
            name={option.label}
            wrapperClassName="w-full"
            onChange={() => handleSelectIncomeType(option.type)}
          />
        ))}
      </div>
    </CombinedIncomeLayout>
  );
};
